import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Plus, Receipt, Trash2 } from 'lucide-react'

import { FieldTooltip } from '@/components/FieldLabel'
import { TeamFeesFields } from '@/components/TeamFeesCard'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  FULL_FEE_TEAM_TYPES,
  newFeeTemplateDraft,
  type FeeTemplateDraft,
} from '@/services/users/teamFees'

interface FeeTemplatesCardProps {
  value: FeeTemplateDraft[]
  onChange: (value: FeeTemplateDraft[]) => void
  /** The network's currencies, default first. */
  currencies: string[]
}

/** Network-wide sets of team fees that super admins can apply to a team. */
export function FeeTemplatesCard({ value, onChange, currencies }: FeeTemplatesCardProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<Set<number>>(new Set())

  const toggle = (key: number) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  const update = (key: number, patch: Partial<FeeTemplateDraft>) =>
    onChange(value.map((d) => (d.key === key ? { ...d, ...patch } : d)))

  return (
    <Card tone="money">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={Receipt} />
          {t('feeTemplates.title')}
          <FieldTooltip text={t('feeTemplates.help')} />
        </CardTitle>
        <CardAction>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const draft = newFeeTemplateDraft()
              onChange([...value, draft])
              setOpen((prev) => new Set(prev).add(draft.key))
            }}
          >
            <Plus className="size-3.5" />
            {t('feeTemplates.add')}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        {value.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('feeTemplates.none')}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {value.map((draft) => {
              const expanded = open.has(draft.key)
              return (
                <div
                  key={draft.key}
                  className="overflow-hidden rounded-lg border border-(--ad-border)"
                >
                  <div className="flex items-center gap-2 p-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0"
                      aria-expanded={expanded}
                      aria-label={expanded ? t('feeTemplates.collapse') : t('feeTemplates.expand')}
                      onClick={() => toggle(draft.key)}
                    >
                      {expanded ? (
                        <ChevronDown className="size-4" />
                      ) : (
                        <ChevronRight className="size-4" />
                      )}
                    </Button>
                    <Input
                      className="sm:max-w-80"
                      value={draft.name}
                      placeholder={t('feeTemplates.namePlaceholder')}
                      aria-label={t('fees.name')}
                      aria-invalid={!draft.name.trim() || undefined}
                      onChange={(e) => update(draft.key, { name: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="ml-auto size-8 shrink-0"
                      aria-label={t('feeTemplates.remove')}
                      title={t('feeTemplates.remove')}
                      onClick={() => onChange(value.filter((d) => d.key !== draft.key))}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                  {expanded && (
                    <div className="border-t border-(--ad-border) bg-(--ad-bg) p-3">
                      <TeamFeesFields
                        value={draft.fees}
                        onChange={(fees) => update(draft.key, { fees })}
                        teamType={FULL_FEE_TEAM_TYPES[0]}
                        currencies={currencies}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
